import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faTimes,
  faClock,
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { Modal } from '../../../components/modal';
import SwitchComponent from '../../../components/switch';
import LoadingSpinner from '../../../components/LoadingSpinner';

// Styled components
const ToggleContainer = styled.div`
  ${tw`flex items-center justify-between mb-6 p-4 bg-bg-secondary rounded-lg transition-all duration-200`}

  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const ToggleLabel = styled.span`
  ${tw`text-text-secondary font-medium`}
`;

const DaysList = styled.div`
  ${tw`space-y-4`}
`;

const DayCard = styled.div`
  ${tw`border border-border-default rounded-lg overflow-hidden shadow-card transition-shadow duration-200`}

  &:hover {
    ${tw`shadow-card`}
  }
`;

const DayHeader = styled.div<{ available: boolean }>`
  ${tw`flex justify-between items-center p-4 cursor-pointer transition-colors duration-200`}
  ${({ available }) => (available ? tw`bg-white` : tw`bg-bg-secondary`)}
  
  ${({ available }) =>
    available
      ? `
        &:hover {
          ${tw`bg-bg-secondary`}
        }
      `
      : ''}
`;

const DayHeaderLeft = styled.div`
  ${tw`flex items-center`}
`;

const DayHeaderRight = styled.div`
  ${tw`flex items-center`}
`;

const DayName = styled.span<{ available: boolean }>`
  ${tw`font-medium ml-3`}
  ${({ available }) => (available ? tw`text-text-primary` : tw`text-text-secondary`)}
`;

const DayTime = styled.span`
  ${tw`text-sm text-text-secondary flex items-center whitespace-nowrap mr-2`}
`;

const DayContent = styled.div`
  ${tw`p-4 border-t border-border-default bg-bg-secondary`}
`;

const TimeRow = styled.div`
  ${tw`flex flex-col sm:flex-row sm:items-center gap-4 mb-4`}
`;

const TimeLabel = styled.label`
  ${tw`text-sm text-text-secondary w-24 font-medium`}
`;

const TimeSelect = styled.select`
  ${tw`flex-1 p-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-black bg-white`}
`;

const AlwaysAvailableRow = styled.div`
  ${tw`flex items-center justify-between mt-4 pt-4 border-t border-border-default`}
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-end gap-4 mt-8`}
`;

const Button = styled.button`
  ${tw`px-6 py-2 rounded-md font-medium focus:outline-none transition-colors duration-200 flex items-center justify-center`}
`;

const CancelButton = styled(Button)`
  ${tw`bg-bg-secondary text-text-primary`}

  &:hover {
    ${tw`bg-gray-300`}
  }
`;

const SaveButton = styled(Button)`
  ${tw`bg-black text-white`}

  &:hover {
    ${tw`bg-blue-700`}
  }
`;

const ApplyContainer = styled.div`
  ${tw`mt-8 p-4 bg-bg-secondary rounded-lg border border-border-default`}
`;

const ApplyTitle = styled.h3`
  ${tw`font-medium mb-2 text-text-secondary flex items-center`}
`;

const DayButtonsContainer = styled.div`
  ${tw`flex flex-wrap gap-2 mt-2`}
`;

const DayButton = styled.button<{ selected: boolean }>`
  ${tw`px-3 py-1 text-sm rounded-md border transition-colors duration-200`}
  ${({ selected }) =>
    selected
      ? tw`bg-black text-white border-black`
      : tw`bg-white text-text-secondary border-border-default`}
  
  ${({ selected }) =>
    !selected
      ? `
        &:hover {
          ${tw`bg-bg-secondary`}
        }
      `
      : ''}
`;

const StatusBadge = styled.span<{ available: boolean }>`
  ${tw`px-2 py-0.5 text-xs rounded-full ml-2`}
  ${({ available }) =>
    available ? tw`bg-green-100 text-green-800` : tw`bg-bg-secondary text-text-primary`}
`;

const ClockIcon = styled(FontAwesomeIcon)`
  ${tw`mr-2 text-text-tertiary`}
`;

const CheckIcon = styled(FontAwesomeIcon)`
  ${tw`mr-2`}
`;

const CheckIconSmall = styled(FontAwesomeIcon)`
  ${tw`ml-1`}
`;

const TimesIcon = styled(FontAwesomeIcon)`
  ${tw`mr-2`}
`;

const ChevronIcon = styled(FontAwesomeIcon)<{ expanded: boolean }>`
  ${tw`text-text-tertiary`}
  transform: ${({ expanded }) => (expanded ? 'rotate(90deg)' : 'rotate(0)')};
  transition: transform 0.2s;
`;

const CheckIconBlue = styled(FontAwesomeIcon)`
  ${tw`mr-2 text-black`}
`;

const LoadingText = styled.span`
  ${tw`ml-2`}
`;

// Add a confirmation dialog component
const ConfirmationDialog = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}
`;

const ConfirmationContent = styled.div`
  ${tw`bg-white rounded-lg p-6 max-w-md mx-auto shadow-overlay`}
`;

const ConfirmationTitle = styled.h3`
  ${tw`text-lg font-medium text-text-primary mb-4 flex items-center`}
`;

const ConfirmationText = styled.p`
  ${tw`text-text-secondary mb-6`}
`;

const ConfirmationButtons = styled.div`
  ${tw`flex justify-end space-x-3`}
`;

const WarningIcon = styled(FontAwesomeIcon)`
  ${tw`text-yellow-500 mr-2`}
`;

// Generate time options in 30-minute increments
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute of [0, 30]) {
      const hourFormatted = hour.toString().padStart(2, '0');
      const minuteFormatted = minute.toString().padStart(2, '0');
      const time = `${hourFormatted}:${minuteFormatted}`;
      const displayTime = `${hour % 12 || 12}:${minuteFormatted} ${hour < 12 ? 'AM' : 'PM'}`;
      options.push({ value: time, label: displayTime });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const formatTimeDisplay = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  return `${hour % 12 || 12}:${minutes} ${hour < 12 ? 'AM' : 'PM'}`;
};

const days = [
  { id: 'sunday', label: 'Sunday' },
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' },
  { id: 'saturday', label: 'Saturday' },
];

interface OperatingHoursFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  hoursData: any;
  expandedDay: string | null;
  selectedDaysToApply: string[];
  alwaysAvailable: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  handleDayClick: (dayId: string) => void;
  handleDayToggle: (dayId: string, isAvailable: boolean) => void;
  handleAlwaysAvailableToggle: (dayId: string, isAlwaysAvailable: boolean) => void;
  handleTimeChange: (dayId: string, field: string, value: string) => void;
  handleDaySelection: (dayId: string) => void;
  applyCurrentDayToSelected: () => void;
  setAlwaysAvailable: (value: boolean) => void;
  handleSave: () => void;
  onNavigateBack: () => void;
  hasUnsavedChanges?: boolean;
  setHasUnsavedChanges?: (value: boolean) => void;
}

const OperatingHoursFormModal: React.FC<OperatingHoursFormModalProps> = ({
  isOpen,
  onClose,
  hoursData,
  expandedDay,
  selectedDaysToApply,
  alwaysAvailable,
  createLoading,
  updateLoading,
  handleDayClick,
  handleDayToggle,
  handleAlwaysAvailableToggle,
  handleTimeChange,
  handleDaySelection,
  applyCurrentDayToSelected,
  setAlwaysAvailable,
  handleSave,
  onNavigateBack,
  hasUnsavedChanges,
  setHasUnsavedChanges,
}) => {
  // Add state for tracking changes and confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Create a reference to the initial data for comparison
  const [initialData, setInitialData] = useState({
    hoursData,
    alwaysAvailable,
  });

  // Update initialData when the modal opens
  useEffect(() => {
    if (isOpen) {
      setInitialData({
        hoursData,
        alwaysAvailable,
      });
      setHasChanges(false);
    }
  }, [isOpen]);

  // Check for changes whenever relevant state changes
  useEffect(() => {
    if (!isOpen) return;

    // Compare current state with initial state
    const hasDataChanges = JSON.stringify(hoursData) !== JSON.stringify(initialData.hoursData);
    const hasAlwaysAvailableChanges = alwaysAvailable !== initialData.alwaysAvailable;

    setHasChanges(hasDataChanges || hasAlwaysAvailableChanges);
  }, [hoursData, alwaysAvailable, isOpen]);

  // Handle close with confirmation if needed
  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  };

  // Handle save and close
  const handleSaveAndClose = () => {
    handleSave();
    setShowConfirmation(false);
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    setShowConfirmation(false);
    onClose();
  };

  // Modify the existing onNavigateBack to use handleClose
  const handleNavigateBack = () => {
    handleClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ToggleContainer>
          <ToggleLabel>Always available</ToggleLabel>
          <SwitchComponent
            isActive={alwaysAvailable}
            onChange={e => setAlwaysAvailable(e.target.checked)}
            inputTestId="operating-hours-always-available-toggle"
          />
        </ToggleContainer>

        {!alwaysAvailable && (
          <DaysList>
            {days.map(day => (
              <DayCard key={day.id}>
                <DayHeader
                  available={hoursData[day.id].available}
                  onClick={() => handleDayClick(day.id)}
                >
                  <DayHeaderLeft>
                    <SwitchComponent
                      isActive={hoursData[day.id].available}
                      onChange={e => handleDayToggle(day.id, e.target.checked)}
                    />
                    <DayName available={hoursData[day.id].available}>
                      {day.label}
                      <StatusBadge available={hoursData[day.id].available}>
                        {hoursData[day.id].available ? 'Available' : 'Unavailable'}
                      </StatusBadge>
                    </DayName>
                  </DayHeaderLeft>
                  <DayHeaderRight>
                    {hoursData[day.id].available && !hoursData[day.id].alwaysAvailable && (
                      <DayTime>
                        <ClockIcon icon={faClock} />
                        {formatTimeDisplay(hoursData[day.id].start)} -{' '}
                        {formatTimeDisplay(hoursData[day.id].end)}
                      </DayTime>
                    )}
                    {hoursData[day.id].available && hoursData[day.id].alwaysAvailable && (
                      <DayTime>
                        <ClockIcon icon={faClock} />
                        Always available
                      </DayTime>
                    )}
                    <ChevronIcon icon={faChevronRight} expanded={expandedDay === day.id} />
                  </DayHeaderRight>
                </DayHeader>

                {expandedDay === day.id && hoursData[day.id].available && (
                  <DayContent>
                    <AlwaysAvailableRow>
                      <ToggleLabel>Always available on {day.label}</ToggleLabel>
                      <SwitchComponent
                        isActive={hoursData[day.id].alwaysAvailable}
                        onChange={e => handleAlwaysAvailableToggle(day.id, e.target.checked)}
                      />
                    </AlwaysAvailableRow>

                    {!hoursData[day.id].alwaysAvailable && (
                      <>
                        <TimeRow>
                          <TimeLabel>Start time</TimeLabel>
                          <TimeSelect
                            value={hoursData[day.id].start}
                            onChange={e => handleTimeChange(day.id, 'start', e.target.value)}
                          >
                            {timeOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </TimeSelect>
                        </TimeRow>

                        <TimeRow>
                          <TimeLabel>End time</TimeLabel>
                          <TimeSelect
                            value={hoursData[day.id].end}
                            onChange={e => handleTimeChange(day.id, 'end', e.target.value)}
                          >
                            {timeOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </TimeSelect>
                        </TimeRow>
                      </>
                    )}

                    <ApplyContainer>
                      <ApplyTitle>
                        <CheckIconBlue icon={faCheck} />
                        Apply these hours to additional days
                      </ApplyTitle>
                      <DayButtonsContainer>
                        {days.map(
                          d =>
                            d.id !== day.id && (
                              <DayButton
                                key={d.id}
                                selected={selectedDaysToApply.includes(d.id)}
                                onClick={() => handleDaySelection(d.id)}
                              >
                                {d.label}
                                {selectedDaysToApply.includes(d.id) && (
                                  <CheckIconSmall icon={faCheck} />
                                )}
                              </DayButton>
                            ),
                        )}
                      </DayButtonsContainer>
                      <ButtonContainer>
                        <SaveButton
                          onClick={applyCurrentDayToSelected}
                          disabled={selectedDaysToApply.length === 0}
                        >
                          Apply to {selectedDaysToApply.length} day
                          {selectedDaysToApply.length !== 1 ? 's' : ''}
                        </SaveButton>
                      </ButtonContainer>
                    </ApplyContainer>
                  </DayContent>
                )}
              </DayCard>
            ))}
          </DaysList>
        )}

        <ButtonContainer>
          <CancelButton onClick={handleNavigateBack}>
            <TimesIcon icon={faTimes} />
            Cancel
          </CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={createLoading || updateLoading}
            data-testid="operating-hours-save-button"
          >
            {createLoading || updateLoading ? (
              <>
                <LoadingSpinner />
                <LoadingText>Saving...</LoadingText>
              </>
            ) : (
              <>
                <CheckIcon icon={faCheck} />
                Save
              </>
            )}
          </SaveButton>
        </ButtonContainer>
      </Modal>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <ConfirmationDialog>
          <ConfirmationContent>
            <ConfirmationTitle>
              <WarningIcon icon={faExclamationTriangle} />
              Unsaved Changes
            </ConfirmationTitle>
            <ConfirmationText>
              You have unsaved changes. Would you like to save them before closing?
            </ConfirmationText>
            <ConfirmationButtons>
              <CancelButton onClick={handleDiscardChanges}>Discard</CancelButton>
              <SaveButton onClick={handleSaveAndClose}>Save Changes</SaveButton>
            </ConfirmationButtons>
          </ConfirmationContent>
        </ConfirmationDialog>
      )}
    </>
  );
};

export default OperatingHoursFormModal;
